import { BrowserRouter, Navigate, Route, Routes } from 'react-router-dom';
import { AuthProvider } from '@/auth/AuthContext';
import { Layout } from '@/components/Layout';
import { ProtectedRoute } from '@/components/ProtectedRoute';
import { RequirePerm } from '@/components/RequirePerm';
import { Login } from '@/pages/Login';
import { Dashboard } from '@/pages/Dashboard';
import { IntakesList } from '@/pages/IntakesList';
import { IntakeDetail } from '@/pages/IntakeDetail';
import { StudiesList } from '@/pages/StudiesList';
import { StudyDetail } from '@/pages/StudyDetail';
import { Tasks } from '@/pages/Tasks';
import { TasksAdmin } from '@/pages/TasksAdmin';
import { LithologyAdmin } from '@/pages/LithologyAdmin';
import { HousingTypeAdmin } from '@/pages/HousingTypeAdmin';
import { RequiredDocsAdmin } from '@/pages/RequiredDocsAdmin';
import { StudyTypeRequiredLayersAdmin } from '@/pages/StudyTypeRequiredLayersAdmin';
import { StudyTypePriceAdmin } from '@/pages/StudyTypePriceAdmin';
import { StudyVariantItemsAdmin } from '@/pages/StudyVariantItemsAdmin';
import { OfferKindsAdmin } from '@/pages/OfferKindsAdmin';
import { FieldWorkKindAdmin } from '@/pages/FieldWorkKindAdmin';
import { TerminyPage } from '@/pages/TerminyPage';
import { AiSectionPromptsAdmin } from '@/pages/AiSectionPromptsAdmin';
import { StudyTypeSectionsAdmin } from '@/pages/StudyTypeSectionsAdmin';
import { ClientsList } from '@/pages/ClientsList';
import { ClientDetail } from '@/pages/ClientDetail';
import { Maps } from '@/pages/Maps';
import { GeorefPage } from '@/pages/GeorefPage';
import { GeorefEditorPage } from '@/pages/GeorefEditorPage';
import { MapOverview } from '@/pages/MapOverview';
import { IntakeWizard } from '@/pages/IntakeWizard';
import { ContractWizard } from '@/pages/ContractWizard';
import { OffersList } from '@/pages/OffersList';
import { OfferDetail } from '@/pages/OfferDetail';
import { ContractDetail } from '@/pages/ContractDetail';
import { OfferWizard } from '@/pages/OfferWizard';
import { AiEditor } from '@/pages/AiEditor';
import { Drive } from '@/pages/Drive';
import { Audit } from '@/pages/Audit';
import { Settings } from '@/pages/Settings';
import { AdminConfig } from '@/pages/AdminConfig';
import { TokenUsage } from '@/pages/TokenUsage';
import { UserManagement } from '@/pages/UserManagement';
import { PermissionsMatrix } from '@/pages/PermissionsMatrix';
import { CompanyManagement } from '@/pages/CompanyManagement';
import { OfferTemplateManagement } from '@/pages/OfferTemplateManagement';
import { Raporty } from '@/pages/Raporty';
import { RaportyOpracowania } from '@/pages/RaportyOpracowania';
import { ZarzadyZlewniList } from '@/pages/ZarzadyZlewniList';
import { ZarzadZlewniDetail } from '@/pages/ZarzadZlewniDetail';
import { AdminArchive } from '@/pages/AdminArchive';
import { AdminTabsLayout } from '@/components/AdminTabsLayout';
import { CardPreviewDev } from '@/pages/CardPreviewDev';
import { PrzekrojPrototypDev } from '@/pages/PrzekrojPrototypDev';
import { BoreholeCardEditor } from '@/pages/BoreholeCardEditor';
import { BoreholeCardsPage } from '@/pages/BoreholeCardsPage';
import { KadryPage } from '@/pages/KadryPage';
import { KadryDetailPage } from '@/pages/KadryDetailPage';
import { PojazdyPage } from '@/pages/PojazdyPage';
import { PojazdyDetailPage } from '@/pages/PojazdyDetailPage';
import { KalendarzPage } from '@/pages/KalendarzPage';
import { DwutygodniowkaPage } from '@/pages/DwutygodniowkaPage';
import { DwutygodniowkaBulkPage } from '@/pages/DwutygodniowkaBulkPage';
import { UrlopyKalendarzPage } from '@/pages/UrlopyKalendarzPage';
import { FakturyPage } from '@/pages/FakturyPage';
import { FakturaDetailPage } from '@/pages/FakturaDetailPage';
import { SubskrypcjePage } from '@/pages/SubskrypcjePage';
import { SubskrypcjaDetailPage } from '@/pages/SubskrypcjaDetailPage';
import { BankPage } from '@/pages/BankPage';
import { SieveAnalysisPage } from '@/pages/SieveAnalysisPage';
import { SieveAnalysisDetailPage } from '@/pages/SieveAnalysisDetailPage';
import { OcenaRyzykaPage } from '@/pages/OcenaRyzykaPage';
import { Asystent } from '@/pages/Asystent';
import { AsystentKorpusy } from '@/pages/AsystentKorpusy';
import { AnalizaFleetPage } from '@/pages/AnalizaFleetPage';
import { OcenaRyzykaDetailPage } from '@/pages/OcenaRyzykaDetailPage';
import { OcenaRyzykaPrzewodnik } from '@/pages/OcenaRyzykaPrzewodnik';
import { PracePage } from '@/pages/PracePage';
import { PracaDetailPage } from '@/pages/PracaDetailPage';
import { PraceHarmonogramPage } from '@/pages/PraceHarmonogramPage';
import { ZleceniaPage } from '@/pages/ZleceniaPage';
import { ZlecenieDetailPage } from '@/pages/ZlecenieDetailPage';
import { McpOAuthConsent } from '@/pages/McpOAuthConsent';

export function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/login" element={<Login />} />
          {/* Podgląd deweloperski renderera karty otworu — tylko w buildzie deweloperskim.
              Stoi poza ProtectedRoute (to jego sens: mock, bez logowania), więc na produkcji
              trasy po prostu nie ma. Dane w mocku pochodzą z realnej karty i nie mają po co
              jechać w publicznym bundlu. */}
          {import.meta.env.DEV && <Route path="/dev/karta" element={<CardPreviewDev />} />}
          {import.meta.env.DEV && <Route path="/dev/przekroj" element={<PrzekrojPrototypDev />} />}
          <Route element={<ProtectedRoute />}>
            <Route path="/mcp/oauth/consent" element={<McpOAuthConsent />} />
            <Route element={<Layout />}>
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="/dashboard" element={<Dashboard />} />
              <Route path="/klienci" element={<RequirePerm perm="CLIENT_VIEW"><ClientsList /></RequirePerm>} />
              <Route path="/klienci/:id" element={<RequirePerm perm="CLIENT_VIEW"><ClientDetail /></RequirePerm>} />
              <Route path="/intakes" element={<RequirePerm perm="INTAKE_VIEW"><IntakesList /></RequirePerm>} />
              <Route path="/intakes/:id" element={<RequirePerm perm="INTAKE_VIEW"><IntakeDetail /></RequirePerm>} />
              <Route path="/karty" element={<RequirePerm perm="BOREHOLE_CARD_VIEW"><BoreholeCardsPage /></RequirePerm>} />
              <Route path="/otwory/:id/karta" element={<RequirePerm perm="BOREHOLE_CARD_VIEW"><BoreholeCardEditor /></RequirePerm>} />
              <Route path="/studies" element={<RequirePerm perm="STUDY_VIEW"><StudiesList /></RequirePerm>} />
              <Route path="/studies/:id" element={<RequirePerm perm="STUDY_VIEW"><StudyDetail /></RequirePerm>} />
              <Route path="/analiza-ryzyka-flota" element={<RequirePerm perm="RISK133_VIEW"><AnalizaFleetPage /></RequirePerm>} />
              <Route path="/analiza-sitowa" element={<RequirePerm perm="SIEVE_VIEW"><SieveAnalysisPage /></RequirePerm>} />
              <Route path="/analiza-sitowa/:id" element={<RequirePerm perm="SIEVE_VIEW"><SieveAnalysisDetailPage /></RequirePerm>} />
              <Route path="/asystent" element={<RequirePerm perm="AI_ASSISTANT_USE"><Asystent /></RequirePerm>} />
              <Route path="/asystent/korpusy" element={<RequirePerm perm="AI_CORPUS_MANAGE"><AsystentKorpusy /></RequirePerm>} />
              <Route path="/ocena-ryzyka" element={<RequirePerm perm="RISK_VIEW"><OcenaRyzykaPage /></RequirePerm>} />
              {/* segment statyczny ma w routerze wyższy priorytet niż /:id, więc „przewodnik" nie wpadnie w kartę oceny */}
              <Route path="/ocena-ryzyka/przewodnik" element={<RequirePerm perm="RISK_VIEW"><OcenaRyzykaPrzewodnik /></RequirePerm>} />
              <Route path="/ocena-ryzyka/:id" element={<RequirePerm perm="RISK_VIEW"><OcenaRyzykaDetailPage /></RequirePerm>} />
              <Route path="/prace" element={<RequirePerm perm="FIELDWORK_VIEW"><PracePage /></RequirePerm>} />
              {/* segment statyczny przed /:id — inaczej „mapa" wpadłaby w kartę pracy.
                  Mapa prac scalona z mapą przeglądu (2026-08): jedna mapa, warstwa „Prace". */}
              <Route path="/prace/mapa" element={<Navigate to="/mapa-przeglad" replace />} />
              <Route path="/prace/harmonogram" element={<RequirePerm perm="FIELDWORK_VIEW"><PraceHarmonogramPage /></RequirePerm>} />
              <Route path="/prace/:id" element={<RequirePerm perm="FIELDWORK_VIEW"><PracaDetailPage /></RequirePerm>} />
              <Route path="/zlecenia" element={<RequirePerm perm="FIELDWORK_VIEW"><ZleceniaPage /></RequirePerm>} />
              <Route path="/zlecenia/:id" element={<RequirePerm perm="FIELDWORK_VIEW"><ZlecenieDetailPage /></RequirePerm>} />
              <Route path="/tasks" element={<RequirePerm perm="TASK_VIEW"><Tasks /></RequirePerm>} />
              <Route path="/terminy" element={<RequirePerm perm="COMPLIANCE_VIEW"><TerminyPage /></RequirePerm>} />
              <Route path="/kalendarz" element={<RequirePerm perm="CALENDAR_VIEW"><KalendarzPage /></RequirePerm>} />
              <Route path="/kadry" element={<RequirePerm perm="HR_VIEW"><KadryPage /></RequirePerm>} />
              <Route path="/kadry/:id" element={<RequirePerm perm="HR_VIEW"><KadryDetailPage /></RequirePerm>} />
              <Route path="/dwutygodniowka" element={<RequirePerm perm="HR_VIEW"><DwutygodniowkaPage /></RequirePerm>} />
              <Route path="/dwutygodniowka-masowo" element={<RequirePerm perm="HR_VIEW"><DwutygodniowkaBulkPage /></RequirePerm>} />
              <Route path="/urlopy" element={<RequirePerm perm="HR_LEAVE_VIEW"><UrlopyKalendarzPage /></RequirePerm>} />
              <Route path="/faktury" element={<RequirePerm perm="FINANCE_VIEW"><FakturyPage /></RequirePerm>} />
              {/* Podgląd faktury — tu prowadzi też powiadomienie „Nowa faktura" (link /faktury/{id}). */}
              <Route path="/faktury/:id" element={<RequirePerm perm="FINANCE_VIEW"><FakturaDetailPage /></RequirePerm>} />
              <Route path="/bank" element={<RequirePerm perm="FINANCE_BANK_VIEW"><BankPage /></RequirePerm>} />
              <Route path="/subskrypcje" element={<RequirePerm perm="FINANCE_VIEW"><SubskrypcjePage /></RequirePerm>} />
              {/* Tu prowadzi powiadomienie o płatności cyklicznej (link /subskrypcje/{id}). */}
              <Route path="/subskrypcje/:id" element={<RequirePerm perm="FINANCE_VIEW"><SubskrypcjaDetailPage /></RequirePerm>} />
              <Route path="/pojazdy" element={<RequirePerm perm="FLEET_VIEW"><PojazdyPage /></RequirePerm>} />
              <Route path="/pojazdy/:id" element={<RequirePerm perm="FLEET_VIEW"><PojazdyDetailPage /></RequirePerm>} />
              <Route path="/mapy" element={<RequirePerm perm="MAPS_DOWNLOAD"><Maps /></RequirePerm>} />
              <Route path="/dopasowanie-map" element={<RequirePerm perm="GEOREF_VIEW"><GeorefPage /></RequirePerm>} />
              <Route path="/dopasowanie-map/:id" element={<RequirePerm perm="GEOREF_VIEW"><GeorefEditorPage /></RequirePerm>} />
              {/* Jedna mapa dla ujęć, klientów i prac — stąd OR na uprawnieniach: mapa prac
                  była dotąd osobną stroną pod FIELDWORK_VIEW. */}
              <Route path="/mapa-przeglad" element={<RequirePerm perm={['INTAKE_VIEW', 'FIELDWORK_VIEW']}><MapOverview /></RequirePerm>} />
              <Route path="/ai-editor" element={<RequirePerm perm="AI_GENERATE"><AiEditor /></RequirePerm>} />
              <Route path="/drive" element={<RequirePerm perm="DRIVE_VIEW"><Drive /></RequirePerm>} />
              <Route path="/intake-wizard" element={<RequirePerm perm="INTAKE_CREATE"><IntakeWizard /></RequirePerm>} />
              <Route path="/contract-wizard" element={<RequirePerm perm="CONTRACT_CREATE"><ContractWizard /></RequirePerm>} />
              <Route path="/oferty" element={<RequirePerm perm="OFFER_VIEW"><OffersList /></RequirePerm>} />
              <Route path="/oferty/:id" element={<RequirePerm perm="OFFER_VIEW"><OfferDetail /></RequirePerm>} />
              <Route path="/umowa/:id" element={<RequirePerm perm="CONTRACT_VIEW"><ContractDetail /></RequirePerm>} />
              <Route path="/oferta-wizard" element={<RequirePerm perm="OFFER_CREATE"><OfferWizard /></RequirePerm>} />
              <Route path="/oferta-wizard/:id" element={<RequirePerm perm="OFFER_CREATE"><OfferWizard /></RequirePerm>} />
              <Route path="/raporty" element={<Navigate to="/raporty/oferty" replace />} />
              <Route path="/raporty/oferty" element={<RequirePerm perm={['REPORTS_VIEW', 'OFFER_VIEW', 'PRICING_VIEW']} mode="all"><Raporty /></RequirePerm>} />
              <Route path="/raporty/opracowania" element={<RequirePerm perm={['REPORTS_VIEW', 'STUDY_VIEW']} mode="all"><RaportyOpracowania /></RequirePerm>} />
              <Route path="/audit" element={<RequirePerm perm="ADMIN_AUDIT"><Audit /></RequirePerm>} />
              <Route path="/settings" element={<Settings />} />
              {/* ── Administracja: zagregowane ekrany z zakładkami ── */}
              <Route path="/admin" element={<Navigate to="/admin/uzytkownicy" replace />} />

              <Route path="/admin/uzytkownicy" element={<RequirePerm perm={['ADMIN_USERS', 'ADMIN_PERMISSIONS']}><AdminTabsLayout group="Użytkownicy i uprawnienia" tabs={[
                { to: '', label: 'Użytkownicy', end: true, requirePerm: 'ADMIN_USERS' },
                { to: 'uprawnienia', label: 'Macierz uprawnień', requirePerm: 'ADMIN_PERMISSIONS' },
                { to: 'raport-zadan', label: 'Raport zadań pracowników', requirePerm: 'ADMIN_CONFIG' },
              ]} /></RequirePerm>}>
                <Route index element={<RequirePerm perm="ADMIN_USERS"><UserManagement /></RequirePerm>} />
                <Route path="uprawnienia" element={<RequirePerm perm="ADMIN_PERMISSIONS"><PermissionsMatrix /></RequirePerm>} />
                <Route path="raport-zadan" element={<RequirePerm perm="ADMIN_CONFIG"><TasksAdmin /></RequirePerm>} />
              </Route>

              <Route path="/admin/konfiguracja" element={<RequirePerm perm="ADMIN_CONFIG"><AdminTabsLayout group="Konfiguracja" tabs={[
                { to: '', label: 'System', end: true },
                { to: 'firmy', label: 'Firmy (nagłówki)' },
                { to: 'tokeny', label: 'Zużycie tokenów AI', requirePerm: 'AI_VIEW_COSTS' },
              ]} /></RequirePerm>}>
                <Route index element={<AdminConfig />} />
                <Route path="firmy" element={<CompanyManagement />} />
                <Route path="tokeny" element={<RequirePerm perm="AI_VIEW_COSTS"><TokenUsage /></RequirePerm>} />
              </Route>

              <Route path="/admin/slowniki" element={<RequirePerm perm="ADMIN_CONFIG"><AdminTabsLayout group="Słowniki opracowań" tabs={[
                { to: '', label: 'Cennik', end: true },
                { to: 'pozycje-kosztorysu', label: 'Pozycje kosztorysu' },
                { to: 'wymagane-dokumenty', label: 'Wymagane dokumenty' },
                { to: 'warstwy-mapowe', label: 'Warstwy per typ' },
                { to: 'sekcje', label: 'Sekcje per typ' },
                { to: 'warstwy', label: 'Typy warstw geol.' },
                { to: 'obudowy', label: 'Typy obudów studni' },
                { to: 'rodzaje-ofert', label: 'Rodzaje ofert' },
              ]} /></RequirePerm>}>
                <Route index element={<StudyTypePriceAdmin />} />
                <Route path="pozycje-kosztorysu" element={<StudyVariantItemsAdmin />} />
                <Route path="wymagane-dokumenty" element={<RequiredDocsAdmin />} />
                <Route path="warstwy-mapowe" element={<StudyTypeRequiredLayersAdmin />} />
                <Route path="sekcje" element={<StudyTypeSectionsAdmin />} />
                <Route path="warstwy" element={<LithologyAdmin />} />
                <Route path="obudowy" element={<HousingTypeAdmin />} />
                <Route path="rodzaje-ofert" element={<OfferKindsAdmin />} />
              </Route>

              {/* Cennik prac terenowych (V186) — osobno od słowników opracowań. */}
              <Route path="/admin/prace-terenowe" element={<RequirePerm perm="ADMIN_CONFIG"><AdminTabsLayout group="Prace terenowe" tabs={[
                { to: '', label: 'Cennik prac', end: true },
              ]} /></RequirePerm>}>
                <Route index element={<FieldWorkKindAdmin />} />
              </Route>

              <Route path="/admin/ai" element={<RequirePerm perm="ADMIN_CONFIG"><AdminTabsLayout group="AI i wzorce" tabs={[
                { to: '', label: 'Prompty AI per sekcja', end: true },
                { to: 'wzorce', label: 'Wzorce ofert (AI)' },
              ]} /></RequirePerm>}>
                <Route index element={<AiSectionPromptsAdmin />} />
                <Route path="wzorce" element={<OfferTemplateManagement />} />
              </Route>

              <Route path="/admin/dane" element={<RequirePerm perm="ADMIN_CONFIG"><AdminTabsLayout group="Dane referencyjne" tabs={[
                { to: '', label: 'Zarządy Zlewni', end: true },
                { to: 'archiwum', label: 'Archiwum RAG', requirePerm: 'ADMIN_RETENTION' },
              ]} /></RequirePerm>}>
                <Route index element={<ZarzadyZlewniList />} />
                <Route path="archiwum" element={<RequirePerm perm="ADMIN_RETENTION"><AdminArchive /></RequirePerm>} />
              </Route>

              {/* Detal Zarządu Zlewni — pełna strona poza zakładkami */}
              <Route path="/admin/zarzady-zlewni/:id" element={<RequirePerm perm="ADMIN_CONFIG"><ZarzadZlewniDetail /></RequirePerm>} />

              {/* Stare adresy → przekierowania (zakładki, bookmarki) */}
              <Route path="/admin/users" element={<Navigate to="/admin/uzytkownicy" replace />} />
              <Route path="/admin/permissions" element={<Navigate to="/admin/uzytkownicy/uprawnienia" replace />} />
              <Route path="/admin/tasks" element={<Navigate to="/admin/uzytkownicy/raport-zadan" replace />} />
              <Route path="/admin/config" element={<Navigate to="/admin/konfiguracja" replace />} />
              <Route path="/admin/firmy" element={<Navigate to="/admin/konfiguracja/firmy" replace />} />
              <Route path="/admin/token-usage" element={<Navigate to="/admin/konfiguracja/tokeny" replace />} />
              <Route path="/admin/cennik" element={<Navigate to="/admin/slowniki" replace />} />
              <Route path="/admin/pozycje-kosztorysu" element={<Navigate to="/admin/slowniki/pozycje-kosztorysu" replace />} />
              <Route path="/admin/required-docs" element={<Navigate to="/admin/slowniki/wymagane-dokumenty" replace />} />
              <Route path="/admin/study-type-sections" element={<Navigate to="/admin/slowniki/sekcje" replace />} />
              <Route path="/admin/lithology" element={<Navigate to="/admin/slowniki/warstwy" replace />} />
              <Route path="/admin/housing-types" element={<Navigate to="/admin/slowniki/obudowy" replace />} />
              <Route path="/admin/ai-section-prompts" element={<Navigate to="/admin/ai" replace />} />
              <Route path="/admin/wzorce-ofert" element={<Navigate to="/admin/ai/wzorce" replace />} />
              <Route path="/admin/zarzady-zlewni" element={<Navigate to="/admin/dane" replace />} />
              <Route path="/admin/archive" element={<Navigate to="/admin/dane/archiwum" replace />} />
            </Route>
          </Route>
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}
